import React, { useMemo, useState } from 'react';
import { Select, Checkbox, Input } from 'antd';
import { SelectProps } from 'antd/lib/select';
import useSettings from 'hooks/useSettings';
import { EnumFields } from 'types/enumerationData';
import { Meta } from 'types/common';
import { sortByProperty } from 'utils/form';
import s from './s.module.less';

type IProps = {
    value?: string[];
    onChange?: (val: string[]) => void;
} & Omit<SelectProps, 'value' | 'onChange' | 'options' | 'mode'>;

// Credentialing Practice 步骤的「Which EHR does your practice currently use?」多选。
// 选项来自 setting type=EHR_TYPE（由 useLoadBasicData 统一拉取存入 SettingEnum store）。
// 值为 dict_key 数组，直接对应接口 ProviderCredentialingPracticeDto.ehrSystems（后端按 CSV 存）。
// 交互对齐原型：下拉面板顶部一个独立搜索框（非选择框内联搜索），下方为带 checkbox 的选项列表。
const EhrSystemSelect = (props: IProps) => {
    const { value, onChange, ...rest } = props;
    const [setting, loading] = useSettings();
    const [search, setSearch] = useState('');
    const selected = value || [];

    const options = useMemo(() => sortByProperty(setting[EnumFields.EHR_TYPE] || [], 'label') as Meta[], [setting]);
    const filtered = useMemo(() => {
        const kw = search.trim().toLowerCase();
        if (!kw) return options;
        return options.filter((o) =>
            String(o.label ?? '')
                .toLowerCase()
                .includes(kw),
        );
    }, [options, search]);

    return (
        <Select
            placeholder="Select"
            {...rest}
            mode="multiple"
            value={value}
            onChange={onChange}
            loading={loading}
            optionLabelProp="label"
            showSearch={false}
            virtual={false}
            menuItemSelectedIcon={null}
            // 关闭时清空搜索关键词，下次打开回到完整列表
            onDropdownVisibleChange={(open) => {
                if (!open) setSearch('');
            }}
            dropdownRender={(menu) => (
                <>
                    {/* 阻止 mousedown 冒泡，避免点击搜索框时下拉收起/选择框抢焦点 */}
                    <div className={s.ehrSearchBox} onMouseDown={(e) => e.stopPropagation()}>
                        <Input
                            allowClear
                            placeholder="Search EHRs..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => e.stopPropagation()}
                        />
                    </div>
                    {menu}
                </>
            )}
        >
            {filtered.map((opt: Meta) => (
                <Select.Option key={opt.value as string} value={opt.value as string} label={opt.label}>
                    <span className={s.ehrOption}>
                        <Checkbox checked={selected.includes(opt.value as string)} />
                        <span>{opt.label}</span>
                    </span>
                </Select.Option>
            ))}
        </Select>
    );
};

export default EhrSystemSelect;
